import React, { useCallback, useRef, useEffect } from 'react';
import { ApolloCache, useApolloClient } from '@apollo/client';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { MediaStatus, UploadStatus, useUploadMediaStatusQuery } from '@graphql/generated';
import { evictMediaCollectionResponse } from '@shared/utils/photoUpload';
import { useTranslation } from '@shared/core';
import { CheckIcon } from '@assets/icons';

interface UsePhotoUploadPollingProps
  extends Readonly<{
    eventId: string;
    collectionId?: string;
    eventHandle?: string;
    updateUploadStatus?: (isUploading: boolean, inProgressCount?: number) => void;
    refetchQueries: string[];
  }> {}

type MediaPhoto = Readonly<{
  __typename: 'MediaPhoto';
  id: string;
  photo: {
    __typename: 'Photo';
    id: string;
    url: string;
    height: number;
    width: number;
    assetId?: string;
  };
}>;

const INITIAL_POLLING_INTERVAL = 1000;
const MAX_POLLING_INTERVAL = 10000;
const BACKOFF_MULTIPLIER = 1.2;
const MAX_ERRORS_BEFORE_STOPPING = 5;

const updateCache = (cache: ApolloCache<{}>, collectionId: string | undefined, eventId: string, newPhotos: MediaPhoto[]) => {
  try {
    if (collectionId) {
      const collectionCacheId = cache.identify({
        __typename: 'MediaCollection',
        id: collectionId,
      });

      if (collectionCacheId) {
        cache.modify({
          id: collectionCacheId,
          fields: {
            media(existingMedia = []) {
              return [...existingMedia, ...newPhotos];
            },
            mediaCount(existingCount = 0) {
              return existingCount + newPhotos.length;
            },
          },
        });
      }
    } else {
      const eventCacheId = cache.identify({
        __typename: 'Event',
        id: eventId,
      });

      if (eventCacheId) {
        cache.modify({
          id: eventCacheId,
          fields: {
            media(existingMedia = []) {
              return [...existingMedia, ...newPhotos];
            },
          },
        });
      }
    }
  } catch (cacheError) {
    console.error('Error updating cache:', cacheError);
  }
};

export const usePhotoUploadPolling = ({ eventId, collectionId, eventHandle, updateUploadStatus, refetchQueries }: UsePhotoUploadPollingProps) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const processedPhotoIdsRef = useRef<Set<string>>(new Set());
  const currentUploadIdRef = useRef<string | null>(null);
  const currentIntervalRef = useRef<number>(INITIAL_POLLING_INTERVAL);
  const errorCountRef = useRef(0);

  const apolloClient = useApolloClient();

  const { tq } = useTranslation('photoUploader');
  const { toast } = useToast();

  const { refetch: fetchUploadMediaStatus } = useUploadMediaStatusQuery({
    batchMode: 'fast',
    skip: true,
  });

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    processedPhotoIdsRef.current.clear();
    // resets the interval to the initial value
    currentIntervalRef.current = INITIAL_POLLING_INTERVAL;
    // resets the error count to 0
    errorCountRef.current = 0;
  }, []);

  /**
   * Polls the server for the status of an ongoing photo upload batch by uploadId.
   * - Updates the cache and UI with completed photos as they become available.
   * - If all photos are uploaded or max error tries is exceeded, triggers stopPolling and shows appropriate toasts.
   *
   * @param uploadId - The identifier for the current upload batch to track.
   * @returns {Promise<boolean>} - Resolves to true to continue polling, or false to stop.
   */
  const poll = useCallback(
    async (uploadId: string): Promise<boolean> => {
      // Early exit if this is not the current upload
      if (currentUploadIdRef.current !== uploadId) {
        return false;
      }
      try {
        const result = await fetchUploadMediaStatus({ eventId, uploadId });

        const uploads = result.data?.uploadMediaStatus?.uploads || [];

        // Collect newly completed photos
        const newPhotos: MediaPhoto[] = [];

        uploads.forEach(upload => {
          if (upload.status === MediaStatus.COMPLETED && upload.photo?.id) {
            const mediaId = upload.id;
            const photo = upload.photo;
            const assetId = upload.assetId;

            if (!mediaId || !photo || processedPhotoIdsRef.current.has(mediaId)) {
              return;
            }

            // Mark as processed
            processedPhotoIdsRef.current.add(mediaId);

            newPhotos.push({
              __typename: 'MediaPhoto',
              id: mediaId,
              photo: {
                __typename: 'Photo',
                id: photo.id,
                url: photo.url,
                height: photo.height,
                width: photo.width,
                assetId,
              },
            });
          }
        });

        // Update cache once with all new photos to show
        if (newPhotos.length > 0) {
          updateCache(apolloClient.cache, collectionId, eventId, newPhotos);
        }

        // Calculate pending uploads count
        const pendingUploads = uploads.filter(u => [MediaStatus.IN_PROGRESS, MediaStatus.NOT_STARTED].includes(u.status)).length;
        updateUploadStatus?.(true, pendingUploads);

        const uploadStatus = result.data?.uploadMediaStatus?.status;
        // Check if all uploads are complete
        if (uploadStatus === UploadStatus.COMPLETED) {
          stopPolling();
          // updates the upload status
          updateUploadStatus?.(false, 0);

          const failedCount = uploads.filter(u => u.status === MediaStatus.FAILED).length || 0;

          if (failedCount > 0) {
            toast(tq`${failedCount} photo(s) failed to upload`, {
              icon: <WarningTriangle />,
            });
          } else {
            toast(tq`All photos successfully uploaded`, {
              icon: <CheckIcon />,
            });
          }

          // Refetch queries to ensure consistency
          await apolloClient.refetchQueries({
            include: refetchQueries,
          });

          // Evict cache to ensure fresh data
          if (eventHandle) {
            evictMediaCollectionResponse({ cache: apolloClient.cache, eventHandle });
          }
          return false;
        }
        return true;
      } catch (error) {
        errorCountRef.current++;
        if (errorCountRef.current >= MAX_ERRORS_BEFORE_STOPPING) {
          stopPolling();
          // updates the upload status
          updateUploadStatus?.(false, 0);
          toast('Unable to fetch upload status. Please refresh the page.', {
            icon: <WarningTriangle />,
          });
          return false;
        }
        return true;
      }
    },
    [apolloClient, collectionId, eventHandle, eventId, fetchUploadMediaStatus, refetchQueries, stopPolling, toast, tq, updateUploadStatus],
  );

  const schedulePoll = useCallback(
    async (uploadId: string) => {
      // Make one polling attempt; 'shouldContinue' indicates if polling should proceed
      const shouldContinue = await poll(uploadId);

      // Only schedule another poll if:
      //   - The poll outcome signals to continue
      //   - The upload hasn't been superseded (defensive against race conditions)
      if (shouldContinue && currentUploadIdRef.current === uploadId) {
        // Exponential backoff for polling interval, up to a max
        currentIntervalRef.current = Math.min(currentIntervalRef.current * BACKOFF_MULTIPLIER, MAX_POLLING_INTERVAL);
        // Clear any scheduled poll to avoid overlap (should not occur but defensive)
        if (pollingIntervalRef.current) {
          clearTimeout(pollingIntervalRef.current);
        }
        // Schedule the next poll after the backoff interval
        pollingIntervalRef.current = setTimeout(() => {
          schedulePoll(uploadId);
        }, currentIntervalRef.current);
      }
    },
    [poll],
  );

  const startPolling = useCallback(
    (uploadId: string) => {
      // Clear any existing polling
      stopPolling();

      currentUploadIdRef.current = uploadId;
      currentIntervalRef.current = INITIAL_POLLING_INTERVAL;
      errorCountRef.current = 0;

      // Reset processed photos
      processedPhotoIdsRef.current.clear();

      // Notify upload started (count unknown at this point)
      updateUploadStatus?.(true);

      // Start polling (first poll immediate, subsequent polls scheduled with backoff)
      schedulePoll(uploadId);
    },
    [schedulePoll, stopPolling, updateUploadStatus],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      // updates the upload status
      updateUploadStatus?.(false, 0);
    };
  }, [stopPolling, updateUploadStatus]);

  return { startPolling, stopPolling };
};
